import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  createMedicalTransportOrder,
  fetchMedicalTransportOrders,
} from '../../redux/slices/medicalTransportOrdersSlice';

// Synthetische Option: Platzhalter-Verordnung anlegen (wie die Schnellanlage im
// RideForm, KONZEPT_KRANKENFAHRT_VERORDNUNGSPFLICHT §8.1).
const CREATE_PLACEHOLDER_OPTION = { id: '__create_placeholder__', isCreatePlaceholder: true };

const formatDay = (value) => {
  if (!value) return '';
  const date = value.toDate ? value.toDate() : new Date(value.seconds ? value.seconds * 1000 : value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('de-DE');
};

/**
 * Reguläre Fahrt im Portal zur Krankenfahrt machen – auch nach dem Abschluss
 * (krankenfahrt_umwandlung_konzept.md §4.3/§4.4). Die Umwandlung schreibt die
 * Callable convertRideToMedicalTransport; Kontingent und Abrechnungsmerkmale
 * holt der Server nach.
 */
const ConvertToMedicalTransportDialog = ({ open, ride, onClose, onConverted }) => {
  const dispatch = useDispatch();
  const { orders, isLoading } = useSelector(state => state.medicalTransportOrders);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      dispatch(fetchMedicalTransportOrders());
      setSelected(null);
      setError('');
    }
  }, [open, dispatch]);

  // Verordnungen des Fahrt-Kunden zuerst.
  const options = useMemo(() => {
    const customerId = ride?.customerId || '';
    const sorted = [...(orders || [])].sort((a, b) => {
      const aOwn = customerId && a.customerId === customerId ? 0 : 1;
      const bOwn = customerId && b.customerId === customerId ? 0 : 1;
      return aOwn - bOwn;
    });
    return [CREATE_PLACEHOLDER_OPTION, ...sorted];
  }, [orders, ride]);

  const isCompleted = ride?.status === 'completed';
  const patientName = (ride?.customerName || '').trim();

  const handleConfirm = async () => {
    if (!selected || !ride) return;
    setSaving(true);
    setError('');
    try {
      let orderId = selected.id;
      if (selected.isCreatePlaceholder) {
        const resultAction = await dispatch(createMedicalTransportOrder({
          patientName,
          customerId: ride.customerId || null,
          patientPhone: ride.customerPhone || '',
          documentStatus: 'incomplete',
        }));
        if (!createMedicalTransportOrder.fulfilled.match(resultAction)) {
          throw new Error(resultAction.payload || 'Platzhalter-Verordnung konnte nicht angelegt werden.');
        }
        orderId = resultAction.payload.id;
      }
      const convert = httpsCallable(functions, 'convertRideToMedicalTransport');
      await convert({ rideId: ride.id, mode: 'convert', medicalTransportOrderId: orderId });
      onConverted?.();
      onClose();
    } catch (convertError) {
      setError(convertError.message || 'Umwandlung fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Zur Krankenfahrt machen</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Die Fahrt wird einer Verordnung zugeordnet und über die Krankenkasse abgerechnet.
          {isCompleted && ' Das Fahrtenkontingent der Verordnung wird sofort belastet, die Abrechnungsmerkmale werden aus dem Fahrer-Track nachgeholt.'}
        </Typography>

        {isCompleted && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Die Fahrt ist bereits abgeschlossen:
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <li>Die Unterschriften des Fahrgasts fehlen – der Nachweis steht auf „fehlt".</li>
              <li>
                Der Fahrer hat {typeof ride?.fare === 'number' ? `${ride.fare.toFixed(2).replace('.', ',')} € ` : 'ggf. den Fahrpreis '}
                als normale Fahrt kassiert. Bitte prüfen, ob der Fahrgast zu viel bezahlt hat und mit der Zuzahlung verrechnet werden muss.
              </li>
            </Box>
          </Alert>
        )}

        <Autocomplete
          options={options}
          loading={isLoading}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => (option.isCreatePlaceholder
            ? '+ Neue Verordnung anlegen (Platzhalter)'
            : `${option.patientName || 'Ohne Patient'}${option.healthInsuranceName ? ` · ${option.healthInsuranceName}` : ''}`)}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box component="li" key={key} {...optionProps}>
                {option.isCreatePlaceholder ? (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    + Neue Verordnung anlegen{patientName ? ` für „${patientName}"` : ''} (Platzhalter)
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{option.patientName || 'Ohne Patient'}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {option.healthInsuranceName || 'Kasse fehlt'}
                        {option.validFrom || option.validUntil
                          ? ` · ${formatDay(option.validFrom)}–${formatDay(option.validUntil)}`
                          : ''}
                      </Typography>
                    </Box>
                    {ride?.customerId && option.customerId === ride.customerId && (
                      <Chip size="small" label="Kunde der Fahrt" color="success" variant="outlined" />
                    )}
                    {option.documentStatus !== 'complete' && (
                      <Chip size="small" label="unvollständig" color="warning" variant="outlined" />
                    )}
                  </Box>
                )}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Verordnung"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {selected?.isCreatePlaceholder && !patientName && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Die Fahrt hat keinen Kundennamen – bitte eine bestehende Verordnung wählen.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || !selected || (selected.isCreatePlaceholder && !patientName)}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Zur Krankenfahrt machen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertToMedicalTransportDialog;
